import React, {
  memo,
  ComponentPropsWithoutRef,
  useState,
  ComponentType,
  forwardRef,
  Ref,
  ReactNode,
} from 'react'

import styled from '@emotion/styled'

import { useHandler } from '@surglogs-app/hooks'
import { Input, TextButton } from '@surglogs-app/ui'

const StyledInput = styled(Input, {
  shouldForwardProp: (prop) => !['isAdornmentVisible'].includes(prop),
})<{ isAdornmentVisible: boolean }>`
  &::-webkit-credentials-auto-fill-button {
    margin-right: ${({ theme, isAdornmentVisible }) =>
      isAdornmentVisible ? theme.ui.spacings.xl : 0};
  }
`

interface InputShowToggleProps extends ComponentPropsWithoutRef<typeof Input> {
  forwardedRef?: Ref<HTMLInputElement> // NOTE: for legacy reasons
  input?: ComponentType<any>
  toggleIcon?: ReactNode
}

export const InputShowToggle = memo(
  forwardRef<HTMLInputElement, InputShowToggleProps>(
    (
      {
        value,
        input: InputComponent = StyledInput,
        forwardedRef,
        'aria-label': ariaLabel,
        toggleIcon,
        ...props
      },
      ref,
    ) => {
      const [visible, setVisible] = useState(false)
      const showValue = useHandler(() => setVisible(true))
      const hideValue = useHandler(() => setVisible(false))

      return (
        <InputComponent
          {...{
            ref: ref ?? forwardedRef,
            adornmentWidth: 60,
            'aria-label': ariaLabel ?? 'textbox-toggle',
            isAdornmentVisible: !!value,
            adornment: (
              <TextButton
                {...{
                  display: value ? 'flex' : 'none',
                  onMouseDown: () => showValue(),
                  onMouseUp: () => hideValue(),
                  onMouseLeave: () => hideValue(),
                  onTouchStart: () => showValue(),
                  onTouchMove: () => hideValue(),
                  onTouchEnd: () => hideValue(),
                }}>
                {toggleIcon}
              </TextButton>
            ),
            type: visible ? 'text' : 'password',
            ...props,
          }}
        />
      )
    },
  ),
)
